/**
 * The hosted app's rago_tv_auth cookie value — Plex's plex_tv_auth shape:
 * one JSON object with the profile fields at the top level next to the
 * session token, URL-encoded on the wire
 * (`%7B%22v%22%3A1%2C%22id%22%3A…%2C%22sessionToken%22%3A…%7D`).
 * The API hands the JSON to its cookie library (which URL-encodes it, as
 * Plex's does); the SPA reads document.cookie and decodes. Both sides go
 * through here so they can never disagree on the layout. The profile is a
 * first-paint cache — GET /v1/me is the truth and re-sets the cookie.
 */
import { z } from "zod";
import { userSchema, type User } from "./auth.js";

export const authCookieSchema = userSchema.extend({
  v: z.literal(1),
  sessionToken: z.string().min(1),
});

export interface AuthCookie {
  user: User;
  sessionToken: string;
}

/** The JSON the API sets (its cookie library URL-encodes it). The cookie is
 * readable by script on the parent domain, so it carries the profile and
 * nothing in flight: a pending email change stays out of it. */
export function authCookieJson(user: User, sessionToken: string): string {
  const { pendingEmail: _pendingEmail, ...profile } = user;
  return JSON.stringify({ v: 1, ...profile, sessionToken });
}

/** The value as it sits in the browser's jar (what document.cookie shows). */
export function serializeAuthCookie(user: User, sessionToken: string): string {
  return encodeURIComponent(authCookieJson(user, sessionToken));
}

/** null for anything that is not a v1 cookie (absent, mangled, older layout).
 * Accepts the wire form (URL-encoded) and the decoded JSON alike. */
export function parseAuthCookie(raw: string | null | undefined): AuthCookie | null {
  if (!raw) return null;
  let json: unknown;
  try {
    json = JSON.parse(decodeURIComponent(raw));
  } catch {
    try {
      json = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const parsed = authCookieSchema.safeParse(json);
  if (!parsed.success) return null;
  const { v: _v, sessionToken, ...user } = parsed.data;
  return { user, sessionToken };
}
